<script setup lang="ts">
/**
 * Module: web/src/components/HelpTip.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 2.0.0
 * Date: 2026-07-11
 *
 * The tooltip atom (decision #142): a small grey ?-in-circle that explains a
 * field or button on hover (desktop) / tap (mobile — hover does not exist on
 * touch, and night dispatch is phone-first). Content comes ONLY from the
 * central registry (web/src/help/tips.ts) keyed by `k`; an unknown key
 * renders NOTHING, so views can reference keys before content lands and
 * pruning a tip is a registry edit, never view surgery.
 *
 * v1.1.0 (Robert's desktop pass): hover is TRANSIENT, click PINS. Hover
 * shows/hides like a normal tooltip; clicking the ? pins the bubble open
 * until a click anywhere else, Escape, or link navigation. Tap on touch =
 * the pin path (outside-tap closes it too).
 *
 * v2.0.0 (tooltip-clipping fix — Robert's Geo screenshot): the bubble was
 * `absolute bottom-full` — ALWAYS above the trigger — so tips near the top
 * of the viewport clipped against the sticky app header, and tips inside
 * overflow panels (map bottom sheet, modal bodies) clipped against their
 * scroll container. Now the bubble TELEPORTS to <body> and positions
 * `fixed` from the trigger's live rect:
 *   - collision-aware side: prefer the configured `side` (default top),
 *     auto-flip when the preferred side lacks room and the other has more;
 *   - the sticky app header is an OBSTRUCTION: available room above =
 *     trigger.top − var(--app-header-h) (AppShell 1.9.0 publishes it);
 *   - horizontal clamp inside the viewport (8px padding);
 *   - worst case (neither side fits the full text) the bubble caps to the
 *     roomier side and scrolls internally — long tips stay fully readable;
 *   - repositions on scroll (capture — inner panels too) and resize while
 *     open; a trigger scrolled fully out of the viewport dismisses.
 * Teleport consequences handled here: hover bridge is now a 140 ms grace
 * timer (the old pb-2 in-DOM bridge can't span a teleport); outside-click
 * checks trigger AND bubble; Tab walks trigger -> link -> onward and
 * Escape/blur return focus sanely; z-[1200] clears Leaflet's controls
 * (z-[800] shimmer) and BaseModal (z-50). Theme vars live on :root /
 * [data-theme], so a body-level bubble keeps light + dark styling.
 *
 * Links: `topic` deep-links the legacy Help section ('/help/<topic>',
 * "Learn more →"); v2.0.0 adds `link` — a full in-app path incl. #anchor
 * (e.g. '/reports/help#geo-render-types', "More →") for the two-layer
 * reporting guide. `link` wins when both are set.
 *
 * Accessibility: focusable button, aria-describedby wiring (IDs resolve
 * document-wide, teleport-safe), Escape unpins + closes + refocuses the
 * trigger, blur closes only when focus leaves both trigger and bubble.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { HELP_TIPS } from '@/help/tips';

const props = withDefaults(defineProps<{
  /** Registry key, e.g. 'roster.is_driver'. Unknown key = renders nothing. */
  k: string;
  /** Preferred bubble side; auto-flips when there is no room. */
  side?: 'top' | 'bottom';
}>(), { side: 'top' });

const router = useRouter();
const entry = computed(() => HELP_TIPS[props.k] ?? null);
const open = ref(false);
/** Click-pinned: survives mouseleave/blur; cleared by outside-click/Escape. */
const pinned = ref(false);
const rootEl = ref<HTMLElement | null>(null);
const btnEl = ref<HTMLButtonElement | null>(null);
const bubbleEl = ref<HTMLElement | null>(null);
const linkEl = ref<HTMLButtonElement | null>(null);
const hoverable = window.matchMedia?.('(hover: hover)').matches ?? true;
const tipId = `helptip-${props.k.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

/** Link target: `link` (in-app path + anchor) wins over legacy `topic`. */
const linkTo = computed(() => {
  if (!entry.value) return null;
  if (entry.value.link) return entry.value.link;
  if (entry.value.topic) return `/help/${entry.value.topic}`;
  return null;
});
const linkLabel = computed(() => (entry.value?.link ? 'More →' : 'Learn more →'));

// ---------------------------------------------------------------------------
// Collision-aware fixed positioning (v2.0.0)
// ---------------------------------------------------------------------------

const GAP = 8;        // trigger <-> bubble gap (the old pb-2)
const PAD = 8;        // viewport padding
const MIN_H = 72;     // readable floor before we give up clamping further

const pos = ref({ top: -9999, left: -9999, maxHeight: 0, ready: false });

/** Sticky-header obstruction: --app-header-h inherits from the AppShell
 *  root (v1.9.0); resolves '' on bare layouts -> 0. */
function headerOffset(): number {
  const el = rootEl.value;
  if (!el) return 0;
  const v = getComputedStyle(el).getPropertyValue('--app-header-h').trim();
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function place(): void {
  const btn = btnEl.value;
  const bubble = bubbleEl.value;
  if (!btn || !bubble) return;
  const r = btn.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Trigger scrolled fully out of the viewport: nothing to anchor to.
  if (r.bottom < 0 || r.top > vh || r.right < 0 || r.left > vw) {
    dismiss();
    return;
  }
  const topEdge = headerOffset() + PAD;
  const bw = bubble.offsetWidth;
  const bh = bubble.scrollHeight;            // natural content height
  const roomAbove = r.top - topEdge - GAP - PAD;
  const roomBelow = vh - PAD - r.bottom - GAP;
  let side = props.side;
  if (side === 'top' && bh > roomAbove && roomBelow > roomAbove) side = 'bottom';
  else if (side === 'bottom' && bh > roomBelow && roomAbove > roomBelow) side = 'top';
  const room = side === 'top' ? roomAbove : roomBelow;
  // Cap to the chosen side's room; the bubble scrolls internally past that.
  const maxH = Math.max(MIN_H, Math.floor(room));
  const h = Math.min(bh, maxH);
  const top = side === 'top' ? r.top - GAP - h : r.bottom + GAP;
  const left = Math.min(Math.max(PAD, r.left + r.width / 2 - bw / 2), vw - bw - PAD);
  pos.value = { top: Math.round(top), left: Math.round(left), maxHeight: maxH, ready: true };
}

function onReflow(): void {
  if (open.value) place();
}

watch(open, (o) => {
  if (o) {
    pos.value = { ...pos.value, ready: false };
    void nextTick(place);
    window.addEventListener('scroll', onReflow, { capture: true, passive: true });
    window.addEventListener('resize', onReflow);
  } else {
    pos.value = { top: -9999, left: -9999, maxHeight: 0, ready: false };
    window.removeEventListener('scroll', onReflow, { capture: true });
    window.removeEventListener('resize', onReflow);
  }
});

// ---------------------------------------------------------------------------
// Hover grace timer (replaces the in-DOM pb-2 bridge the teleport removed)
// ---------------------------------------------------------------------------

let hideTimer: ReturnType<typeof setTimeout> | null = null;

function cancelHide(): void {
  if (hideTimer !== null) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
}
function scheduleHide(): void {
  if (!hoverable || pinned.value) return;
  cancelHide();
  hideTimer = setTimeout(() => {
    if (!pinned.value) open.value = false;
  }, 140);
}

function show(): void {
  if (hoverable) {
    cancelHide();
    open.value = true;
  }
}

/** Click/tap toggles the PIN; a pinned bubble ignores hover-out and blur. */
function toggle(): void {
  pinned.value = !pinned.value;
  open.value = pinned.value;
}

function dismiss(): void {
  pinned.value = false;
  open.value = false;
  cancelHide();
}

function dismissAndRefocus(): void {
  dismiss();
  btnEl.value?.focus();
}

// ---------------------------------------------------------------------------
// Focus + outside-click (teleport-aware: bubble is NOT inside rootEl)
// ---------------------------------------------------------------------------

function insideUs(target: EventTarget | null): boolean {
  const n = target as Node | null;
  return !!n && (!!rootEl.value?.contains(n) || !!bubbleEl.value?.contains(n));
}

function onBlur(e: FocusEvent): void {
  if (pinned.value) return;
  if (insideUs(e.relatedTarget)) return;   // focus moved into the bubble
  open.value = false;
}

function onDocPointerdown(e: PointerEvent): void {
  if (!insideUs(e.target)) dismiss();
}
watch(pinned, (p) => {
  if (p) document.addEventListener('pointerdown', onDocPointerdown, true);
  else document.removeEventListener('pointerdown', onDocPointerdown, true);
});

/** Tab from the trigger enters the teleported link (DOM order can't). */
function onTriggerTab(e: KeyboardEvent): void {
  if (e.shiftKey || !open.value || !linkEl.value) return;
  e.preventDefault();
  linkEl.value.focus();
}

/** Tab off the link resumes the page's natural order from the trigger;
 *  Shift+Tab returns to the trigger. */
function onLinkTab(e: KeyboardEvent): void {
  if (e.shiftKey) {
    e.preventDefault();
    btnEl.value?.focus();
    return;
  }
  // Refocus the trigger, let default Tab advance from there (old DOM order).
  btnEl.value?.focus();
  if (!pinned.value) open.value = false;
}

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocPointerdown, true);
  window.removeEventListener('scroll', onReflow, { capture: true });
  window.removeEventListener('resize', onReflow);
  cancelHide();
});

function learnMore(): void {
  const to = linkTo.value;
  dismiss();
  if (to) void router.push(to);
}
</script>

<template>
  <span
    v-if="entry"
    ref="rootEl"
    class="relative inline-flex align-middle"
    @mouseenter="show"
    @mouseleave="scheduleHide"
    @keydown.escape="dismissAndRefocus"
  >
    <button
      ref="btnEl"
      type="button"
      class="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full text-fg-muted/80 hover:text-fg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
      :aria-describedby="open ? tipId : undefined"
      :aria-expanded="open"
      aria-label="What is this?"
      :data-tip-open="open ? 'true' : undefined"
      @focus="show"
      @blur="onBlur"
      @click.stop.prevent="toggle"
      @keydown.tab="onTriggerTab"
    >
      <svg
        viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round" class="w-[15px] h-[15px]"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M9.6 9.3a2.5 2.5 0 1 1 3.6 2.5c-.8.4-1.2 1-1.2 1.9v.3" />
        <circle cx="12" cy="16.8" r="0.5" />
      </svg>
    </button>

    <!-- Body-teleported so no overflow ancestor / sticky header / transformed
         container can clip it; fixed coords come from place() above. -->
    <Teleport to="body">
      <span
        v-if="open"
        :id="tipId"
        ref="bubbleEl"
        role="tooltip"
        class="fixed z-[1200] block rounded-lg border border-border-c bg-card shadow-2xl p-3 text-left w-max max-w-[min(18rem,calc(100vw-16px))] overflow-y-auto overscroll-contain"
        :style="{
          top: `${pos.top}px`,
          left: `${pos.left}px`,
          maxHeight: pos.maxHeight ? `${pos.maxHeight}px` : undefined,
          visibility: pos.ready ? 'visible' : 'hidden',
        }"
        @mouseenter="cancelHide"
        @mouseleave="scheduleHide"
        @keydown.escape.stop="dismissAndRefocus"
      >
        <span class="block text-xs leading-relaxed text-fg font-normal whitespace-normal">{{ entry.text }}</span>
        <button
          v-if="linkTo"
          ref="linkEl"
          type="button"
          class="mt-1.5 text-xs font-medium text-accent hover:underline"
          @click.stop="learnMore"
          @blur="onBlur"
          @keydown.tab="onLinkTab"
        >{{ linkLabel }}</button>
      </span>
    </Teleport>
  </span>
</template>
